import React, { useState, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import enUS from 'date-fns/locale/en-US';
import 'react-datepicker/dist/react-datepicker.css';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import TimePickerV2 from '@/components/TimePicker/TimePickerV2';
import CustomCheckbox from '@/components/CustomCheckbox';
import { SaveApiResMessage } from '@/actions';

// Register the US locale
registerLocale('en-US', enUS);

const priorityOptions = [
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
  { label: 'Exclude', value: 'EXCLUDE' },
];
const priorityOptionsWithNone = [
  { label: 'None', value: 'NONE' },
  { label: 'High', value: 'HIGH' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'Low', value: 'LOW' },
  { label: 'Exclude', value: 'EXCLUDE' },
];

const MinimalPrioritySelect = ({ value, onChange, withNone = false }) => (
  <CustomSelectEx
    key={value || 'empty'} // Force re-render when value changes
    options={withNone ? priorityOptionsWithNone : priorityOptions}
    value={
      value ? priorityOptions.find((opt) => opt.value === value) || null : null
    }
    onChange={(opt) => onChange(opt?.value || '')}
    placeholder="Select Priority"
    width={'-webkit-fill-available'}
    dropdown={true}
  />
);

const isSameDay = (d1, d2) =>
  d1 &&
  d2 &&
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const normalizeDate = (date) => {
  if (!date) return null;
  // Simply create a new date with just year, month, day to avoid timezone issues
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

// Helper function to parse date string in US format
const parseDateString = (dateString) => {
  if (!dateString) return null;
  // Handle YYYY-MM-DD format
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  return new Date(dateString);
};

const WeekdayPreferences = ({
  weekdayPrefs,
  setWeekdayPrefs,
  selectedRanges,
  setSelectedRanges,
  lfdDate,
}) => {
  // State for draft selection: [startDate, endDate]
  const [rangeDraft, setRangeDraft] = useState([null, null]);
  // State for draft time selection: [startTime, endTime]
  const [rangeDraftTime, setRangeDraftTime] = useState([null, null]);
  // State for hover date
  const [hoverDate, setHoverDate] = useState(null);
  // State for priority
  const [rangePriority, setRangePriority] = useState('');
  // State for all day checkbox - default to true
  const [isAllDay, setIsAllDay] = useState(true);
  // State for error messages
  const [timeErrors, setTimeErrors] = useState({ from: null, to: null });

  // Ref for the datepicker container
  const datepickerRef = useRef(null);

  // Helper to sort dates
  const sortDates = (d1, d2) => (d1 < d2 ? [d1, d2] : [d2, d1]);

  // Helper function to convert time string to minutes for comparison
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;

    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to check if two time ranges overlap
  const timeRangesOverlap = (start1, end1, start2, end2) => {
    if (!start1 || !end1 || !start2 || !end2) return false;

    const start1Min = timeToMinutes(start1);
    const end1Min = timeToMinutes(end1);
    const start2Min = timeToMinutes(start2);
    const end2Min = timeToMinutes(end2);

    return start1Min < end2Min && start2Min < end1Min;
  };

  // Comprehensive overlap validation function
  const checkForOverlaps = (
    newStart,
    newEnd,
    newTimeStart,
    newTimeEnd,
    isNewAllDay
  ) => {
    const checkEnd = newEnd || newStart;

    for (const existingRange of selectedRanges) {
      const existingStart = parseDateString(existingRange.date_start);
      const existingEnd = parseDateString(existingRange.date_end);
      const existingTimeStart = existingRange.time_start;
      const existingTimeEnd = existingRange.time_end;
      const isExistingAllDay = !existingTimeStart && !existingTimeEnd;

      // Check if date ranges overlap
      const dateOverlap = newStart <= existingEnd && checkEnd >= existingStart;

      if (dateOverlap) {
        // If both are all day, they overlap
        if (isNewAllDay && isExistingAllDay) {
          const existingPriority =
            priorityOptions.find((opt) => opt.value === existingRange.priority)
              ?.label || existingRange.priority;
          return {
            hasOverlap: true,
            message: `Overlaps with existing frame: ${formatDate(
              existingStart
            )}${
              !isSameDay(existingStart, existingEnd)
                ? ` to ${formatDate(existingEnd)}`
                : ''
            } (${existingPriority}).`,
          };
        }

        // If new is all day and existing has time, they overlap
        if (isNewAllDay && !isExistingAllDay) {
          const existingPriority =
            priorityOptions.find((opt) => opt.value === existingRange.priority)
              ?.label || existingRange.priority;
          return {
            hasOverlap: true,
            message: `Overlaps with existing frame: ${formatDate(
              existingStart
            )}${
              !isSameDay(existingStart, existingEnd)
                ? ` to ${formatDate(existingEnd)}`
                : ''
            }, ${existingTimeStart}–${existingTimeEnd} (${existingPriority}).`,
          };
        }

        // If existing is all day and new has time, they overlap
        if (!isNewAllDay && isExistingAllDay) {
          const existingPriority =
            priorityOptions.find((opt) => opt.value === existingRange.priority)
              ?.label || existingRange.priority;
          return {
            hasOverlap: true,
            message: `Overlaps with existing frame: ${formatDate(
              existingStart
            )}${
              !isSameDay(existingStart, existingEnd)
                ? ` to ${formatDate(existingEnd)}`
                : ''
            } (${existingPriority}).`,
          };
        }

        // If both have time ranges, check time overlap
        if (
          !isNewAllDay &&
          !isExistingAllDay &&
          newTimeStart &&
          newTimeEnd &&
          existingTimeStart &&
          existingTimeEnd
        ) {
          if (
            timeRangesOverlap(
              newTimeStart,
              newTimeEnd,
              existingTimeStart,
              existingTimeEnd
            )
          ) {
            const existingPriority =
              priorityOptions.find(
                (opt) => opt.value === existingRange.priority
              )?.label || existingRange.priority;
            return {
              hasOverlap: true,
              message: `Overlaps with existing frame: ${formatDate(
                existingStart
              )}${
                !isSameDay(existingStart, existingEnd)
                  ? ` to ${formatDate(existingEnd)}`
                  : ''
              }, ${existingTimeStart}–${existingTimeEnd} (${existingPriority}).`,
            };
          }
        }
      }
    }

    return { hasOverlap: false };
  };

  // Add selected range to list
  const handleAddRange = () => {
    const [start, end] = rangeDraft;
    if (start && rangePriority) {
      // Clear previous errors
      setTimeErrors({ from: null, to: null });

      // Validate time fields when All day is false
      if (!isAllDay) {
        const fromTime = rangeDraftTime[0];
        const toTime = rangeDraftTime[1];
        let hasTimeError = false;
        const newErrors = { from: null, to: null };

        if (!fromTime) {
          newErrors.from = 'Please select a start time';
          hasTimeError = true;
        }
        if (!toTime) {
          newErrors.to = 'Please select an end time';
          hasTimeError = true;
        }

        // Check if both times are selected and validate time order
        if (fromTime && toTime) {
          const fromMinutes = timeToMinutes(fromTime);
          const toMinutes = timeToMinutes(toTime);
          // Only validate time order if it's the same day
          if (
            (rangeDraft[0] && !rangeDraft[1]) ||
            isSameDay(rangeDraft[0], rangeDraft[1])
          ) {
            // Allow overnight ranges (e.g., 12:00 to 00:00 means 12:00 PM to 12:00 AM next day)
            // Only show error if the end time is before start time AND it's not midnight (00:00)
            if (fromMinutes > toMinutes && toMinutes !== 0) {
              newErrors.to = 'End time must be after start time';
              hasTimeError = true;
            }
          }
        }

        if (hasTimeError) {
          setTimeErrors(newErrors);
          return;
        }
      }

      const checkEnd = end || start; // Use start as end if no end selected
      const newTimeStart = isAllDay ? null : rangeDraftTime[0]?.toString();
      const newTimeEnd = isAllDay ? null : rangeDraftTime[1]?.toString();

      // Check for overlaps
      const overlapCheck = checkForOverlaps(
        start,
        checkEnd,
        newTimeStart,
        newTimeEnd,
        isAllDay
      );

      if (overlapCheck.hasOverlap) {
        SaveApiResMessage({
          title: 'Error',
          message: overlapCheck.message,
          mode: 'error',
        });
        return;
      }

      // If no overlap, add the range
      setSelectedRanges((prev) => [
        ...prev,
        {
          date_start: formatDateYYYYMMDD(start),
          date_end: formatDateYYYYMMDD(end || start), // Use start as end if no end selected
          priority: rangePriority.toUpperCase(),
          time_start: newTimeStart,
          time_end: newTimeEnd,
        },
      ]);

      setRangeDraft([null, null]);
      setRangeDraftTime([null, null]);
      setHoverDate(null);
      setRangePriority(''); // Reset priority to show placeholder
      setIsAllDay(true); // Reset to all day by default
      setTimeErrors({ from: null, to: null }); // Clear errors on successful add
    }
  };

  const handleDeleteRange = (index) => {
    setSelectedRanges((prev) => prev.filter((_, i) => i !== index));
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toUpperCase()) {
      case 'NONE':
        return 'priority-none';
      case 'HIGH':
        return 'priority-high';
      case 'MEDIUM':
        return 'priority-medium';
      case 'LOW':
        return 'priority-low';
      case 'EXCLUDE':
        return 'priority-exclude';
      default:
        return '';
    }
  };

  // Date selection logic (like hour selection)
  const handleDayClick = (date) => {
    if (!rangeDraft[0]) {
      setRangeDraft([date, null]);
    } else if (rangeDraft[0] && !rangeDraft[1]) {
      if (isSameDay(date, rangeDraft[0])) {
        setRangeDraft([null, null]);
        setHoverDate(null);
        return;
      }

      const [start, end] = sortDates(rangeDraft[0], date);

      // Check for overlap with existing ranges
      let hasOverlap = false;
      for (const r of selectedRanges) {
        const existingStart = parseDateString(r.date_start);
        const existingEnd = parseDateString(r.date_end);
        if (start <= existingEnd && end >= existingStart) {
          hasOverlap = true;
          break;
        }
      }

      if (hasOverlap) {
        // If overlap found, just reset the selection
        setRangeDraft([null, null]);
        setHoverDate(null);
        return;
      }

      // If no overlap, set the range
      setRangeDraft([start, end]);
    } else {
      setRangeDraft([date, null]);
      setHoverDate(null);
    }
  };

  // Hover logic
  const handleDayMouseEnter = (date) => {
    if (rangeDraft[0] && !rangeDraft[1]) {
      setHoverDate(date);
    }
  };
  const handleDayMouseLeave = () => setHoverDate(null);

  // Class logic for each day
  const getDayClasses = (date) => {
    const d = normalizeDate(date);
    let classes = [];

    // Check if this is lfd-day
    const isLfdDay = lfdDate && isSameDay(d, normalizeDate(new Date(lfdDate)));
    if (isLfdDay) {
      classes.push('lfd-day');
    }

    // Check for multiple time ranges on the same day
    const dayRanges = selectedRanges.filter((r) => {
      const s = normalizeDate(parseDateString(r.date_start));
      const e = normalizeDate(parseDateString(r.date_end));
      return d >= s && d <= e;
    });

    if (dayRanges.length > 0) {
      // If there are multiple ranges on the same day, check for split priorities
      const uniquePriorities = [...new Set(dayRanges.map((r) => r.priority))];

      if (uniquePriorities.length > 1) {
        // Multiple different priorities on same day - use split display
        classes.push('priority-split');

        // Sort ranges by time to determine left/right order
        // For all-day ranges, treat as 00:00 (start of day)
        // For time ranges, use the actual start time
        const sortedRanges = dayRanges.sort((a, b) => {
          const timeA = a.time_start ? timeToMinutes(a.time_start) : 0;
          const timeB = b.time_start ? timeToMinutes(b.time_start) : 0;
          return timeA - timeB;
        });

        if (uniquePriorities.length === 2) {
          // Two priorities - use existing 50/50 split
          const firstPriority = sortedRanges[0].priority.toLowerCase();
          const lastPriority =
            sortedRanges[sortedRanges.length - 1].priority.toLowerCase();

          // Create time-based split classes instead of priority-based
          // First priority (earliest time) goes on left, last priority (latest time) goes on right
          classes.push(`priority-split-time-${firstPriority}-${lastPriority}`);
        } else if (uniquePriorities.length === 3) {
          // Three priorities - use 1/3 split
          const firstPriority = sortedRanges[0].priority.toLowerCase();
          const middlePriority =
            sortedRanges[
              Math.floor(sortedRanges.length / 2)
            ].priority.toLowerCase();
          const lastPriority =
            sortedRanges[sortedRanges.length - 1].priority.toLowerCase();

          // Create three-way split class
          classes.push(
            `priority-split-three-${firstPriority}-${middlePriority}-${lastPriority}`
          );
        } else {
          // More than 3 priorities - fallback to first and last (50/50)
          const firstPriority = sortedRanges[0].priority.toLowerCase();
          const lastPriority =
            sortedRanges[sortedRanges.length - 1].priority.toLowerCase();
          classes.push(`priority-split-time-${firstPriority}-${lastPriority}`);
        }
      } else {
        // Single priority for the day
        classes.push(getPriorityColor(dayRanges[0].priority));
      }
    } else {
      // Weekday priority (if no range selected for this date)
      const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const weekday = days[d.getDay()];
      if (weekdayPrefs && weekdayPrefs[weekday]) {
        const prioClass = getPriorityColor(weekdayPrefs[weekday]);
        if (prioClass) classes.push(prioClass);
      }
    }
    // Final selection (draft)
    const [start, end] = rangeDraft;
    const nStart = normalizeDate(start);
    const nEnd = normalizeDate(end);
    if (nStart && nEnd && d >= nStart && d <= nEnd) {
      classes.push('hour-selected');
      if (isSameDay(d, nStart)) classes.push('hour-start');
      else if (isSameDay(d, nEnd)) classes.push('hour-end');
      else classes.push('hour-between');
    }
    // Only start selected (single click)
    else if (nStart && !nEnd && isSameDay(d, nStart)) {
      classes.push('hour-selected', 'hour-start');
    }
    // Hover selection
    else if (nStart && !nEnd && hoverDate) {
      const nHover = normalizeDate(hoverDate);
      const [hStart, hEnd] =
        nHover < nStart ? [nHover, nStart] : [nStart, nHover];
      if (d >= hStart && d <= hEnd) {
        classes.push('hour-hover');
        if (isSameDay(d, hStart)) classes.push('hour-hover-start');
        else if (isSameDay(d, hEnd)) classes.push('hour-hover-end');
        else classes.push('hour-hover-between');
      }
    }

    return classes.join(' ');
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}-${day}-${year}`;
  };

  const formatDateYYYYMMDD = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Disable add button
  const isAddButtonDisabled = () => {
    const [start] = rangeDraft;
    return !start || !rangePriority;
  };

  const handleClickOutsideCalender = (event) => {
    const isInsideDatepicker =
      datepickerRef.current && datepickerRef.current.contains(event.target);
    const isSelectElement = event.target.closest('#dropdown-container');
    const isTimePickerElement = event.target.closest(
      '#time-picker-v2-container'
    );

    if (isInsideDatepicker) {
      return;
    }
    if (isSelectElement) {
      return;
    }
    if (isTimePickerElement) {
      return;
    } else {
      setRangeDraft([null, null]);
      setHoverDate(null);
    }
  };

  return (
    <div className="weekday-preferences">
      <div className="weekday-section">
        <div className="weekday-grid">
          {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((day) => (
            <div key={day} className="weekday-box">
              <span className="weekday-label">
                {day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()}
              </span>
              <MinimalPrioritySelect
                withNone={true}
                value={weekdayPrefs[day]}
                onChange={(val) =>
                  setWeekdayPrefs({ ...weekdayPrefs, [day]: val })
                }
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div
          id="datepicker-container"
          ref={datepickerRef}
          className="min-w-400 flex-basis-full">
          <div className="max-w-524 bg-white border border-solid border-border-primary shadow-date-picker rounded px-3 py-4">
            {lfdDate && (
              <div className="last-free-day">
                Your Last Free Day:
                <span className="last-free-day-bold">{lfdDate}</span>
              </div>
            )}
            <DatePicker
              inline
              selectsRange
              startDate={rangeDraft[0] || undefined}
              endDate={rangeDraft[1] || undefined}
              filterDate={(date) => {
                // Allow navigation to previous months, but disable dates before today
                const today = normalizeDate(new Date());
                const dateToCheck = normalizeDate(date);
                return dateToCheck >= today;
              }}
              onChange={(update) => setRangeDraft(update)}
              dayClassName={getDayClasses}
              onDayClick={handleDayClick}
              onDayMouseEnter={handleDayMouseEnter}
              onDayMouseLeave={handleDayMouseLeave}
              selected={undefined}
              locale="en-US"
              onClickOutside={handleClickOutsideCalender}
            />
            <div className="border-b-0 border-l-0 border-r-0 border-t border-solid border-border-tertiary pt-3">
              <p className="m-0 text-sm text-primary-text mb-2 font-averta">
                Add Time
              </p>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-11 text-primary-text">From:</span>

                <TimePickerV2
                  value={rangeDraftTime[0]}
                  onChange={(time) => {
                    setRangeDraftTime([time, rangeDraftTime[1]]);

                    if (time && timeErrors.from) {
                      setTimeErrors((prev) => ({ ...prev, from: null }));
                    }
                  }}
                  placeholder="From 00:00"
                  error={timeErrors.from}
                  disabled={isAllDay}
                />

                <span className="text-11 text-primary-text">To:</span>

                <TimePickerV2
                  value={rangeDraftTime[1]}
                  onChange={(time) => {
                    setRangeDraftTime([rangeDraftTime[0], time]);

                    if (time && timeErrors.to) {
                      setTimeErrors((prev) => ({ ...prev, to: null }));
                    }
                  }}
                  placeholder="To 00:00"
                  error={timeErrors.to}
                  disabled={isAllDay}
                />

                <span>
                  <CustomCheckbox
                    checked={isAllDay}
                    labelClass="text-secondary-text text-11 whitespace-nowrap"
                    onChange={() => {
                      setIsAllDay(!isAllDay);
                      if (!isAllDay) {
                        setRangeDraftTime([null, null]);
                      } else {
                        setTimeErrors({ from: null, to: null });
                        setRangeDraftTime(['00:00', '00:00']);
                      }
                    }}
                    label="All Day"
                  />
                </span>
              </div>
            </div>
            <div className="calendar-actions">
              <MinimalPrioritySelect
                value={rangePriority}
                onChange={setRangePriority}
              />
              <button
                className="btn"
                onClick={handleAddRange}
                disabled={isAddButtonDisabled()}>
                + Add to List
              </button>
            </div>
          </div>
        </div>
        <div className="calendar-list ">
          <span className="section-title">Your Selected Weekdays:</span>
          <div className="range-list">
            {selectedRanges.map((range, i) => {
              const start = parseDateString(range.date_start);
              const end = parseDateString(range.date_end);
              const isSameDay =
                start.getFullYear() === end.getFullYear() &&
                start.getMonth() === end.getMonth() &&
                start.getDate() === end.getDate();
              return (
                <div key={i} className="range-item">
                  <span className="text-11 text-inactive-text">
                    {isSameDay
                      ? `${formatDate(start)} ${
                          range.time_start
                            ? `,${range.time_start} to ${range.time_end}`
                            : ''
                        }`
                      : `${formatDate(start)} ${
                          range.time_start ? `,${range.time_start}` : ''
                        } 
                        to ${formatDate(end)} ${
                          range.time_end ? `,${range.time_end}` : ''
                        }`}
                    : {range.priority}
                  </span>
                  <button
                    onClick={() => handleDeleteRange(i)}
                    className="remove">
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekdayPreferences;
